import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../App';
import useLogger from '../hooks/useLogger';

function UploadPage({ onUploadSuccess }) {
  const logger = useLogger();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    logger.trackPageVisit('upload');
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    await uploadFiles(files);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    await uploadFiles(files);
  };

  const uploadFiles = async (files) => {
    const excelFiles = files.filter(
      file => file.name.endsWith('.xlsx') || file.name.endsWith('.xls')
    );

    if (excelFiles.length === 0) {
      setMessage({
        type: 'error',
        text: 'Lütfen geçerli bir Excel dosyası (.xlsx veya .xls) seçin.'
      });
      return;
    }

    setUploading(true);
    setMessage(null);

    const results = [];

    for (const file of excelFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${getApiUrl()}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        results.push({
          fileName: file.name,
          success: true,
          examInfo: response.data.examInfo
        });
        
        // Track file upload
        logger.trackFileUpload(file.name, file.size);
      } catch (error) {
        results.push({
          fileName: file.name,
          success: false,
          error: error.response?.data?.error || error.message
        });
      }
    }

    setUploadedFiles(results);
    setUploading(false);

    const successCount = results.filter(r => r.success).length;
    if (successCount > 0) {
      setMessage({
        type: 'success',
        text: `${successCount} dosya başarıyla yüklendi!`
      });
      if (onUploadSuccess) {
        onUploadSuccess();
      }
    } else {
      setMessage({
        type: 'error',
        text: 'Dosyalar yüklenirken hata oluştu.'
      });
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900">Dosya Yükle</h2>
        <p className="mt-2 text-gray-600">
          Deneme sonuçlarınızı içeren Excel dosyalarını buraya yükleyin. Birden fazla dosya aynı anda yükleyebilirsiniz.
        </p>
      </div>

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
          isDragging
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 bg-white hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept=".xlsx,.xls"
          onChange={handleFileSelect}
        />

        {uploading ? (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <p className="mt-4 text-lg text-gray-700">Yükleniyor...</p>
          </div>
        ) : (
          <>
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="mt-4 text-lg text-gray-700">
              Dosyaları buraya sürükleyin veya{' '}
              <button
                onClick={handleButtonClick}
                className="text-primary-600 hover:text-primary-700 font-semibold"
              >
                dosya seçin
              </button>
            </p>
            <p className="mt-2 text-sm text-gray-500">
              Excel dosyaları (.xlsx, .xls)
            </p>
          </>
        )}
      </div>

      {/* Message */}
      {message && (
        <div
          className={`mt-4 p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-100 border border-green-400 text-green-700'
              : 'bg-red-100 border border-red-400 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Uploaded Files */}
      {uploadedFiles.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Yükleme Sonuçları:
          </h3>
          <div className="space-y-2">
            {uploadedFiles.map((file, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border ${
                  file.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">
                      {file.success ? '✓' : '✗'}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{file.fileName}</p>
                      {file.success && file.examInfo && (
                        <p className="text-sm text-gray-600">
                          Deneme: {file.examInfo.examNumber} | Tarih: {file.examInfo.examDate} | 
                          Öğrenci Sayısı: {file.examInfo.studentCount}
                        </p>
                      )}
                      {!file.success && file.error && (
                        <p className="text-sm text-red-600">{file.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-blue-900 mb-3">
          📝 Kullanım Talimatları:
        </h4>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start">
            <span className="mr-2">1.</span>
            <span>Excel dosyalarınızı hazırlayın (standart deneme takip formatında)</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">2.</span>
            <span>Dosyaları yukarıdaki alana sürükleyip bırakın veya dosya seçin butonunu kullanın</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">3.</span>
            <span>Birden fazla haftalık denemeyi aynı anda yükleyebilirsiniz</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">4.</span>
            <span>Yükleme tamamlandıktan sonra Ana Sayfa'dan sonuçları görüntüleyin</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default UploadPage;

